import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, MapPin, Phone, Mail } from "lucide-react";
import { useLang, LangToggle } from "@/lib/i18n";
import { PostAppointmentReview } from "@/components/reviews/PostAppointmentReview";
import { WhatsAppFab } from "@/components/WhatsAppFab";
import { InstallButton, InstallBanner } from "@/components/InstallApp";
import { readPending } from "@/lib/pending";

const NAV = [
  { to: "/today", label: "nav.today", id: "nav-today" },
  { to: "/horoscope", label: "nav.chart", id: "nav-horoscope" },
  { to: "/kundli-milan", label: "nav.milan", id: "nav-kundli-milan" },
  { to: "/book", label: "nav.book", id: "nav-book" },
  { to: "/reviews", label: "nav.reviews", id: "nav-reviews" },
  { to: "/about", label: "nav.about", id: "nav-about" },
];

const Brand = () => (
  <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
    <img src="/brand/logo-sm.webp" alt="Shri Vidya Jyotish logo" className="h-11 w-11 rounded-full ring-1 ring-[#D4AF37]/60 object-cover" />
    <div className="leading-tight">
      <div className="font-display text-base sm:text-lg gold-text font-semibold">Shri Vidya Jyotish</div>
      <div className="font-deva text-xs text-[#E8C55A]/80">श्रीविद्या ज्योतिष</div>
    </div>
  </Link>
);

const navCls = ({ isActive }) =>
  `text-sm tracking-wide transition-colors ${isActive ? "text-[#F3DC8B]" : "text-slate-300 hover:text-[#F3DC8B]"}`;

const Header = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-[#D4AF37]/15 bg-[#070B14]/95">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-20 flex items-center justify-between">
        <Brand />
        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={navCls} data-testid={n.id}>{t(n.label)}</NavLink>
          ))}
          <LangToggle />
          <InstallButton testid="header-install-button" />
          <Link to="/book" className="btn-gold px-5 py-2.5 text-sm" data-testid="header-book-cta">{t("nav.bookNow")}</Link>
        </nav>
        <div className="md:hidden flex items-center gap-3">
          <LangToggle />
          <button className="text-[#F3DC8B]" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-[#D4AF37]/15 px-5 py-4 flex flex-col gap-4 bg-[#070B14]">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={navCls} onClick={() => setOpen(false)} data-testid={`${n.id}-mobile`}>{t(n.label)}</NavLink>
          ))}
          <InstallButton testid="mobile-install-button" className="self-start" />
        </div>
      )}
    </header>
  );
};

const Footer = () => {
  const { t } = useLang();
  return (
  <footer className="border-t border-[#D4AF37]/15 mt-24">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 py-12 grid md:grid-cols-3 gap-8 text-sm text-slate-400">
      <div>
        <div className="font-display gold-text text-lg mb-2">Shri Vidya Jyotish</div>
        <p>{t("footer.tagline")}</p>
        <div className="mt-5 grid gap-4">
          {[["amol", "+918329454656", "+91-8329454656"], ["aditya", "+919850559397", "+91-9850559397"]].map(([id, tel, shown]) => (
            <div key={id} data-testid={`footer-person-${id}`}>
              <div className="text-slate-100 font-medium">{t(`name.${id}`)}</div>
              <div className="text-[#D4AF37] text-xs tracking-wide">{t(`desig.${id}`)}</div>
              <a href={`tel:${tel}`} className="hover:text-[#F3DC8B]">{shown}</a>
            </div>
          ))}
        </div>
      </div>
      <div className="flex gap-2" data-testid="footer-address">
        <MapPin size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />
        <span>{t("address")}</span>
      </div>
      <div className="grid gap-2 content-start">
        <div className="flex gap-2" data-testid="footer-phone">
          <Phone size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />
          <div className="grid gap-1 content-start">
            <a href="tel:+918329454656" className="hover:text-[#F3DC8B]" data-testid="footer-phone-amol">+91-8329454656</a>
            <a href="tel:+919850559397" className="hover:text-[#F3DC8B]" data-testid="footer-phone-aditya">+91-9850559397</a>
          </div>
        </div>
        <a href="mailto:consult@shreevidyajyotish.com" className="flex items-center gap-2 hover:text-[#F3DC8B]" data-testid="footer-email"><Mail size={16} className="text-[#D4AF37]" /> consult@shreevidyajyotish.com</a>
        <Link to="/about" className="hover:text-[#F3DC8B] mt-2" data-testid="footer-about-link">About Us</Link>
        <Link to="/privacy" className="hover:text-[#F3DC8B] mt-2" data-testid="footer-privacy-link">Privacy Policy</Link>
      </div>
    </div>
  </footer>
  );
};

export default function Layout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (readPending() && pathname === "/") { navigate("/book", { replace: true }); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return (
    <div className="min-h-screen bg-[#070B14] text-slate-200 relative">
      <div className="pointer-events-none fixed inset-0 starfield opacity-60" />
      <div className="pointer-events-none fixed inset-0 starfield-twinkle" />
      <Header />
      <PostAppointmentReview />
      <WhatsAppFab />
      <InstallBanner />
      <main className="relative pb-16">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
